import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const additionalKeys = {
  all: ['additionals'],
  lists: () => [...additionalKeys.all, 'list'],
  details: () => [...additionalKeys.all, 'detail'],
  detail: (id) => [...additionalKeys.details(), id],
};

export const useAdditionals = () => {
  return useQuery({
    queryKey: additionalKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('additionals');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useAdditional = (id, options = {}) => {
  return useQuery({
    queryKey: additionalKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`additionals/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateAdditional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (additionalData) => {
      const response = await apiClient.post('additionals', additionalData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: additionalKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating additional:', error);
    },
  });
};

export const useUpdateAdditional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`additionals/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(additionalKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: additionalKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating additional:', error);
    },
  });
};

export const useDeleteAdditional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`additionals/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: additionalKeys.lists() });
      queryClient.removeQueries({ queryKey: additionalKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting additional:', error);
    },
  });
};
